import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { format } from "date-fns";
import { Check, Copy } from "lucide-react";
import type { ProvablyFairRoundData } from "../../services/roundResultsService";

type ProvablyFairDetailsProps = {
  isOpen: boolean;
  onClose: () => void;
  roundData: ProvablyFairRoundData;
  onOpenProvablyFairModal?: () => void;
};

const ProvablyFairDetails: React.FC<ProvablyFairDetailsProps> = ({
  isOpen,
  onClose,
  roundData,
  onOpenProvablyFairModal,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hashVerification, setHashVerification] = useState<
    "checking" | "verified" | "mismatch" | "unavailable"
  >("unavailable");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setCopiedKey(null);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isOpen) return;

    const serverSeed = roundData.provablyFair.serverSeed?.trim();
    const expectedHash = roundData.provablyFair.serverSeedHash
      ?.trim()
      .toLowerCase();

    if (
      !serverSeed ||
      !expectedHash ||
      serverSeed === "N/A" ||
      expectedHash === "n/a" ||
      !window.crypto?.subtle
    ) {
      setHashVerification("unavailable");
      return;
    }

    let cancelled = false;
    setHashVerification("checking");

    window.crypto.subtle
      .digest("SHA-256", new TextEncoder().encode(serverSeed))
      .then((digest) => {
        if (cancelled) return;

        const actualHash = Array.from(new Uint8Array(digest))
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("");
        setHashVerification(
          actualHash === expectedHash ? "verified" : "mismatch",
        );
      })
      .catch(() => {
        if (!cancelled) setHashVerification("unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    roundData.provablyFair.serverSeed,
    roundData.provablyFair.serverSeedHash,
  ]);

  if (!isVisible && !isOpen) return null;

  const createdAt = new Date(roundData.createdAt);
  const formattedTime = Number.isNaN(createdAt.getTime())
    ? "--:--:--"
    : format(createdAt, "HH:mm:ss");

  const getPlayerLabel = (index: number) => {
    switch (index) {
      case 0:
        return "Player N1:";
      case 1:
        return "Player N2:";
      case 2:
        return "Player N3:";
      default:
        return `Player N${index + 1}:`;
    }
  };

  const copyWithSelection = (value: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.style.position = "absolute";
    textArea.style.top = `${window.scrollY}px`;
    textArea.style.left = "-9999px";
    textArea.style.width = "1px";
    textArea.style.height = "1px";
    textArea.style.fontSize = "16px";
    document.body.appendChild(textArea);

    const handleCopy = (event: ClipboardEvent) => {
      if (!event.clipboardData) return;
      event.preventDefault();
      event.clipboardData.setData("text/plain", value);
    };
    document.addEventListener("copy", handleCopy);

    try {
      textArea.focus({ preventScroll: true });
      textArea.select();
      textArea.setSelectionRange(0, value.length);
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      document.removeEventListener("copy", handleCopy);
      document.body.removeChild(textArea);
    }
  };

  const showCopied = (key: string) => {
    setCopiedKey(key);
    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current);
    }
    copyResetTimerRef.current = setTimeout(() => setCopiedKey(null), 1600);
  };

  const copyValue = (value: string, key: string) => {
    if (!value || value === "N/A") return;

    // Paint the tick before any browser clipboard operation can block.
    flushSync(() => showCopied(key));

    // Never run both writers together: concurrent writes can race and make
    // the clipboard appear to work only after repeated clicks.
    if (navigator.clipboard && window.isSecureContext) {
      void navigator.clipboard.writeText(value).catch(() => {
        copyWithSelection(value);
      });
      return;
    }

    copyWithSelection(value);
  };

  const CopyValueButton = ({
    value,
    copyKey,
    label,
  }: {
    value: string;
    copyKey: string;
    label: string;
  }) => {
    const copied = copiedKey === copyKey;

    return (
      <button
        type="button"
        onClick={() => copyValue(value, copyKey)}
        disabled={!value || value === "N/A"}
        className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
          copied
            ? "text-green-400 bg-green-500/10"
            : "text-[#97a4ae] hover:text-white hover:bg-white/10"
        }`}
        aria-label={`Copy ${label}`}
        title={copied ? "Copied" : `Copy ${label}`}
      >
        {copied ? <Check size={17} /> : <Copy size={17} />}
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[1550]"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`absolute top-[0.5%] left-2 right-2 mb-2 font-['Inter'] duration-300 md:max-w-[500px] md:min-w-[500px] md:left-[16.5%] m820:left-[18.6%] m910:left-[21.8%] lg:min-w-[800px] lg:max-w-[800px] lg:left-[22.3%] bg-[rgb(37,40,48)] max-h-fit border-transparent border-0 rounded-lg ${
          isOpen
            ? "top-[1.1%] s375:top-[1.2%] s390:top-[0.9%] s540:top-[1.1%] md:top-[2.8%] m820:top-[2.4%] m910:top-[2.1%] lg:top-[2%] xl:top-[3%] fadeIn"
            : "pointer-events-none fadeOut"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header - EXACT Angular Design */}
      <div className="bg-[#2c2d30] px-2.5 border-0 rounded-t-lg h-9 flex justify-between items-center w-full">
        <div className="flex flex-row justify-center items-center gap-1">
          <h3 className="text-base text-white tracking-[0.008em] font-['Inter'] font-light">
            ROUND {roundData.roundId}
          </h3>
          <div className="flex flex-row justify-center items-center gap-[7px]">
            <div
              className="cursor-pointer font-semibold text-[12px] py-0.5 px-1.5 bg-[#00000080] border-0 rounded-[11px] box-border"
              style={{ color: "#34B4FF" }}
            >
              {roundData.multiplier}x
            </div>
            <div className="text-white font-light text-[14px]">
              {formattedTime}
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="text-[#97a4ae] -translate-y-0.5 cursor-pointer text-[24px] font-bold font-['Nunito'] leading-none opacity-50 ![text-shadow:_0_1px_0_#97a4ae]"
        >
          ×
        </button>
      </div>

      {/* Content - EXACT Angular Layout */}
      <div className="p-4 lg:px-12 w-full bg-[#1b1c1d] max-h-fit lg:max-h-[88svh] overflow-auto">
        {/* Server Seed Section */}
        <div className="flex justify-start gap-2.5 items-center">
          <div>
            <img
              className="w-8 h-[31px]"
              src="/icon-provabyfair.svg"
              alt="server"
            />
          </div>
          <div className="leading-none tracking-[0.008em]">
            <h4 className="text-[14px] text-white font-light">Server Seed:</h4>
            <p className="text-[14px] mt-[3px] text-[#ffffff80] font-light">
              Generated on our side
            </p>
          </div>
        </div>
        <div className="w-full mt-2 relative">
          <input
            className="bg-[#0707084d] w-full text-[14px] pl-5 pr-12 py-3 font-light border-0 rounded-md text-white"
            type="text"
            value={roundData.provablyFair.serverSeed}
            readOnly
            onFocus={(event) => event.currentTarget.select()}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <CopyValueButton
              value={roundData.provablyFair.serverSeed}
              copyKey="server-seed"
              label="server seed"
            />
          </div>
        </div>

        {/* Client Seed Section */}
        <div className="flex justify-start gap-2.5 items-center mt-[18px]">
          <div>
            <img
              className="w-8 h-[25px]"
              src="/icon-provabyfair.svg"
              alt="client"
            />
          </div>
          <div className="leading-none tracking-[0.008em]">
            <h4 className="text-[14px] text-white font-light">Client Seed:</h4>
            <p className="text-[14px] mt-[3px] text-[#ffffff80] font-light">
              Generated on players side
            </p>
          </div>
        </div>

        {/* Client Seed List - EXACT Angular Layout */}
        {roundData.provablyFair.clientSeed.map((item, index) => (
          <div
            key={index}
            className="bg-[#0707084d] px-5 pt-1 mb-[5px] border-0 rounded-md mt-2 h-[56px] lg:h-[43px] lg:flex lg:justify-start lg:items-center gap-20"
          >
            <div className="flex flex-row justify-center items-center gap-2">
              <div className="text-[#ffffff80] text-[14px] font-light">
                {getPlayerLabel(index)}
              </div>
              <div>
                <img
                  className="w-[30px] h-[30px]"
                  src={
                    index === 0 ? "/avatars/av-4.png" : "/avatars/av-2.png"
                  }
                  alt={`user_${index}`}
                />
              </div>
              <div className="text-white leading-[14px] text-[14px]">
                {item.name}
              </div>
            </div>
            <div className="flex flex-row items-center gap-2.5 flex-1 min-w-0">
              <div className="text-[#ffffff80] text-[14px] font-light">
                Seed:
              </div>
              <div className="text-white text-[14px] font-light tracking-[0.01em] truncate select-all">
                {item.seed}
              </div>
              <CopyValueButton
                value={item.seed}
                copyKey={`client-seed-${index}`}
                label={`client seed ${index + 1}`}
              />
            </div>
          </div>
        ))}

        {/* Server Seed Hash Section */}
        <div className="flex justify-start p-4 lg:px-12 bg-[#0707084d] gap-2.5 items-center mt-[25px] w-full">
          <img
            className="w-8 h-[30px]"
            src="/icon-provabyfair.svg"
            alt="hash"
          />
          <div className="leading-none tracking-[0.008em] flex-1 min-w-0">
            <h4 className="text-[14px] text-white font-light">
              Server Seed SHA256 Hash:
            </h4>
            <p className="text-[14px] mt-[3px] text-[#ffffff80] font-light break-all select-all">
              {roundData.provablyFair.serverSeedHash}
            </p>
            <p
              className={`text-[12px] mt-2 ${
                hashVerification === "verified"
                  ? "text-green-500"
                  : hashVerification === "mismatch"
                    ? "text-red-400"
                    : "text-[#ffffff80]"
              }`}
            >
              {hashVerification === "checking"
                ? "Verifying commitment…"
                : hashVerification === "verified"
                  ? "SHA-256 commitment verified"
                  : hashVerification === "mismatch"
                    ? "Warning: revealed seed does not match the commitment"
                    : "Commitment verification unavailable"}
            </p>
          </div>
          <CopyValueButton
            value={roundData.provablyFair.serverSeedHash}
            copyKey="server-seed-hash"
            label="server seed hash"
          />
        </div>

        {/* Proof and Result - EXACT Angular Grid */}
        <div className="lg:flex lg:flex-row justify-center gap-3 items-center w-full">
          <div className="text-center mt-[15px] lg:mt-0 w-full">
            <h3 className="mb-1 text-[#ffffff80] text-[14px] font-light">
              Proof:
            </h3>
            <div className="w-full mt-2 relative">
              <input
                className="bg-[#0707084d] w-full text-[14px] pl-5 pr-12 py-3 font-light border-0 rounded-md text-white"
                type="text"
                value={roundData.provablyFair.proof}
                readOnly
                onFocus={(event) => event.currentTarget.select()}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <CopyValueButton
                  value={roundData.provablyFair.proof}
                  copyKey="proof"
                  label="proof"
                />
              </div>
            </div>
          </div>

          <div className="text-center w-full lg:mt-3">
            <h3 className="mb-1 text-[#ffffff80] text-[14px] font-light">
              Result:
            </h3>
            <div className="bg-[#0707084d] text-white flex justify-center items-center text-[14px] font-['Inter'] mb-1.5 py-3 font-light border-0 rounded-md max-h-[38px]">
              {roundData.provablyFair.crashPoint}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - EXACT Angular Design */}
      <div className="bg-[#2c2d30] border-0 rounded-b-lg w-full h-[70px] xxm:h-[48px] font-['Inter'] flex justify-center items-center p-2.5">
        <div className="m-1 text-[14px] text-[#ffffff80] font-light text-center">
          For instructions check{" "}
          <button
            onClick={onOpenProvablyFairModal}
            className="text-[#e30c32] cursor-pointer bg-transparent border-none p-0"
          >
            What is Provably Fair
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProvablyFairDetails;
